import React, { useEffect } from 'react';
import {
  Button,
  LoadingButton,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Typography,
  Link,
} from '@one-thd/sui-atomic-components';
import { setContactOverlayAnalytics } from '../../../utils/payments-analytics';
import { IModalBasicProps } from '../../../models/common/modal';
import { CardType } from '../../../constants/payments';
import { IPaymentCardCommonData } from '../../../models/common/payments';
import { getCardImagePath } from '../../../utils/credit-cards';

export type ISetInactiveAcceptedCardTypes =
  | CardType.CREDIT_CARD
  | CardType.GIFT_CARD;

// may eventually change type to Record<CardType ...>
const headerMappings: Record<
  ISetInactiveAcceptedCardTypes,
  (card: IPaymentCardCommonData) => string
> = {
  [CardType.CREDIT_CARD]: () => {
    return 'Do You Want To Set This Card As Inactive?';
  },
  [CardType.GIFT_CARD]: () => {
    return 'Do You Want To Set This Card As Inactive?';
  },
};

// may eventually change type to Record<CardType ...>
const buttonMappings: Record<
  ISetInactiveAcceptedCardTypes,
  (card: IPaymentCardCommonData) => string
> = {
  [CardType.CREDIT_CARD]: () => {
    return 'Yes, Set Card as Inactive';
  },
  [CardType.GIFT_CARD]: () => {
    return 'Yes, Set Card as Inactive';
  },
};

const childrenMapping: Record<
  ISetInactiveAcceptedCardTypes,
  (
    card: IPaymentCardCommonData,
    isB2B: boolean,
    hasHDWallet: boolean,
  ) => React.ReactNode
> = {
  [CardType.CREDIT_CARD]: (card, isB2B, hasHDWallet) => {
    const {
      cardBrand,
      cardType,
      cardNumberLast4,
      cardExpiryMonth,
      cardExpiryYear,
      citiAccountId,
    } = card;
    if (citiAccountId) {
      return (
        <>
          <Typography weight="bold">
            Contact Citi directly to set this card inactive. This action
            can&apos;t be undone once it&apos;s complete.
          </Typography>
          <br />
          <Typography>
            Call the Citi customer service number located on the back of your
            card or visit your{' '}
            <Link href={cardType === 'HR' ? '/MyCRC' : '/Mycard'}>
              online Citi account.
            </Link>
          </Typography>
          <br />
          <Typography>
            You will continue to have access to purchase history associated with
            this card, but you will not be able to make new purchases with this
            card.
          </Typography>
        </>
      );
    }
    return (
      <>
        <Typography variant="body-base" weight="bold">
          You will not be able to use this card for online purchases
          {hasHDWallet ? ', HD Pass ' : ' '}
          or remote purchase authorization via phone or Text2Confirm.
        </Typography>
        <br />
        <div className="sui-flex sui-gap-2">
          <img
            width={33}
            height="auto"
            src={getCardImagePath(cardBrand, cardType)}
            alt="Credit card logo"
          />
          <Typography> ****{cardNumberLast4}</Typography>
          <Typography>
            {cardExpiryMonth &&
              cardExpiryYear &&
              `Exp ${cardExpiryMonth}/${cardExpiryYear.slice(-2)}`}
          </Typography>
        </div>
        <br />
        <Typography>
          You can still make purchases in store with the physical card and view
          purchase history in your account.
        </Typography>
      </>
    );
  },
  [CardType.GIFT_CARD]: () => {
    return (
      <Typography variant="body-base">
        You&apos;ll still be able to review all purchase history associated with
        this gift card in your account.
      </Typography>
    );
  },
};

export interface ISetInactiveProps {
  card: IPaymentCardCommonData;
  onConfirmSetInactive: () => void;
  isLoading: boolean;
  cardType: ISetInactiveAcceptedCardTypes;
  isB2B: boolean;
  hasHDWallet: boolean;
}

export const SetInactiveModal = ({
  card,
  hideModal,
  onConfirmSetInactive,
  isLoading,
  cardType,
  isB2B,
  hasHDWallet,
}: ISetInactiveProps & IModalBasicProps) => {
  const headerText = headerMappings[cardType](card);
  const buttonText = buttonMappings[cardType](card);
  const children = childrenMapping[cardType](card, isB2B, hasHDWallet);

  useEffect(() => {
    if (card.citiAccountId) {
      setContactOverlayAnalytics();
    }
  }, [card.citiAccountId]);

  return (
    <>
      <ModalHeader onClose={hideModal}>{headerText}</ModalHeader>
      <ModalBody>{children}</ModalBody>
      <ModalFooter>
        {card.citiAccountId ? (
          <div className="sui-flex sui-whitespace-nowrap sui-gap-4 sm:sui-gap-4 sui-flex-col-reverse sui-w-full sm:sui-flex-row">
            <Button variant="primary" fullWidth onClick={hideModal}>
              Got It
            </Button>
          </div>
        ) : (
          <div className="sui-flex sui-whitespace-nowrap sui-gap-4 sm:sui-gap-4 sui-flex-col-reverse sui-w-full sm:sui-flex-row">
            <Button variant="secondary" fullWidth onClick={hideModal}>
              No
            </Button>
            <LoadingButton
              variant="primary"
              loading={isLoading}
              fullWidth
              onClick={onConfirmSetInactive}
              data-testid={`${cardType}-set-inactive-confirm-btn`}
            >
              {buttonText}
            </LoadingButton>
          </div>
        )}
      </ModalFooter>
    </>
  );
};

SetInactiveModal.displayName = 'SetInactiveModal';
